import { useState, useEffect, useCallback } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useLang } from "@/lib/i18n";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";

export const AdminAnnouncements = ({ assoc }) => {
  const { t, lang } = useLang();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", content: "" });

  const load = useCallback(() => {
    api.get(`/associations/${assoc.slug}/announcements`).then(({ data }) => setItems(data));
  }, [assoc.slug]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    try {
      await api.post(`/associations/${assoc.slug}/announcements`, form);
      toast.success(t("saved"));
      setOpen(false);
      setForm({ title: "", content: "" });
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t("confirm_delete"))) return;
    await api.delete(`/associations/${assoc.slug}/announcements/${id}`);
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-heading font-extrabold text-2xl tracking-tight">{t("announcements")}</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-announcement-btn" className="rounded-full active:scale-95">
              <Plus className="h-4 w-4 mr-2" /> {t("add_announcement")}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-heading">{t("add_announcement")}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label>{t("title")}</Label>
                <Input data-testid="announcement-form-title" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
              </div>
              <div className="space-y-1.5">
                <Label>{t("content")}</Label>
                <Textarea rows={4} data-testid="announcement-form-content" value={form.content} onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))} />
              </div>
              <Button data-testid="announcement-form-submit" onClick={submit} disabled={!form.title || !form.content} className="w-full rounded-full active:scale-95">
                {t("create")}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-3" data-testid="admin-announcements-list">
        {items.map((a) => (
          <div key={a.id} className="border border-border rounded-lg bg-card p-5 flex justify-between gap-4">
            <div>
              <p className="font-heading font-semibold">{a.title}</p>
              <p className="text-sm text-muted-foreground mt-1">{a.content}</p>
              <p className="text-[10px] font-mono-data text-muted-foreground mt-2">
                {new Date(a.created_at).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-US", { day: "numeric", month: "long", year: "numeric" })}
              </p>
            </div>
            <Button variant="ghost" size="icon" data-testid="delete-announcement-btn" onClick={() => remove(a.id)} className="h-8 w-8 text-destructive shrink-0">
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
};
